import React, { memo } from 'react';
import { useStyles } from 'uinix-ui';

import { MenuActions } from '../../types/MenuActions';
import { TextVariant } from '../../types/TextVariant';
import { UserAction } from '../../types/UserAction';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';

interface Props {
  /** Array of link actions in the ordered list of nav breadcrumbs */
  actions: UserAction[];
  /** Optional text variant for breadcrumbs */
  textVariant?: TextVariant;
  /** Optional prop to align the breadcrumbs by the text contents instead of the button container */
  enableTextAlignment?: boolean;
}

const MAX_VISIBLE_ACTIONS = 4;

export const Breadcrumbs = memo(
  ({ actions, textVariant, enableTextAlignment }: Props) => {
    const styles = useStyles();

    const limit = Math.min(actions.length, MAX_VISIBLE_ACTIONS);
    const visibleActions = actions.slice(0, limit);
    const hiddenActions = actions.slice(limit);

    return (
      <nav>
        <Layout as="ol" styles={styles.list.unset}>
          {actions.length > MAX_VISIBLE_ACTIONS && (
            <li>
              <ActionsMenu
                actions={toMenuActions(hiddenActions)}
                name="more actions"
              />
            </li>
          )}
          {visibleActions.map((action, i) => {
            const { hash, href, pathname, state, text, tooltip, onClick } =
              action;
            const isLastAction = i === visibleActions.length - 1;
            const isLink = Boolean(href || pathname);
            const enableSeparator = (i === 0 && hiddenActions.length) || i > 0;
            const disabled = Boolean(action.disabled || isLastAction);

            const crumb = (
              <Button
                textVariant={textVariant}
                disabled={disabled}
                shouldTruncate
                text={text}
                tooltip={tooltip}
                variant="tertiary"
                onClick={onClick}
              />
            );

            return (
              <Layout
                as="li"
                key={i}
                styles={
                  enableTextAlignment
                    ? { marginLeft: -4, paddingRight: 4 }
                    : undefined
                }
              >
                {enableSeparator && (
                  <Icon disabled={disabled} icon="chevron-right" px={0.5} />
                )}
                {isLink ? (
                  <Link
                    disabled={disabled}
                    hash={hash}
                    href={href}
                    pathname={pathname}
                    state={state}
                  >
                    {crumb}
                  </Link>
                ) : (
                  crumb
                )}
              </Layout>
            );
          })}
        </Layout>
      </nav>
    );
  },
);

const toMenuActions = (actions: UserAction[]): MenuActions<undefined> =>
  actions.map((link) => ({
    ...link,
    label: link.text,
    value: undefined,
  }));
